import React, { useMemo, useState } from "react";
import DealsHeader from "./Deals/DealsHeader";
import CustomTableDeals from "./Deals/CustomTableDeals";
import CreateDeal from "./Deals/CreateDeal";
import DealDetails from "./Deals/DealDetails";
import Loader from "@/shared/components/ui/Loaders/Loader";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import NoDataTable from "@/shared/components/ui/NoDataView/NoDataTable";
import NoDataCommonCard from "@/shared/components/ui/NoDataView/NoDataCommon";
import moneyGif from "@/assets/images/money.gif";
import {
  useCreateAccountDeal,
  useDeleteAccountDeal,
  useFetchAccountDeals,
  useUpdateAccountDeal,
} from "@/features/accounts/hooks/useAccounts";

const EMPTY_DEALS = [];

const Deals = ({ companyIdentifier, companyName }) => {
  const [showCreateDeal, setShowCreateDeal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [dealToEdit, setDealToEdit] = useState(null);
  const [dealToDelete, setDealToDelete] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const { data: dealsData, isLoading, isError } = useFetchAccountDeals({
    companyIdentifier,
  });
  const { mutate: createDeal, isPending: isCreating } = useCreateAccountDeal({
    companyIdentifier,
    action: () => setShowCreateDeal(false),
  });
  const { mutate: updateDeal, isPending: isUpdating } = useUpdateAccountDeal({
    companyIdentifier,
    action: () => setDealToEdit(null),
  });
  const { mutate: deleteDeal, isPending: isDeleting } = useDeleteAccountDeal({
    companyIdentifier,
    action: () => setDealToDelete(null),
  });

  const deals = dealsData?.deals ?? EMPTY_DEALS;

  const filteredDeals = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return deals;
    return deals.filter((deal) =>
      [deal.name, deal.pipeline, deal.status, deal.owner?.name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search)),
    );
  }, [deals, searchValue]);

  const [selectedDeals, setSelectedDeals] = useState([]);

  const openCount = useMemo(() => {
    return deals.filter((deal) => deal.status === "in_process").length;
  }, [deals]);

  const totalValue = useMemo(() => {
    return deals.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0);
  }, [deals]);

  // ── View state — mirrors the Campaigns pattern ──
  const hasSearch = searchValue.trim().length > 0;
  const viewState = (() => {
    if (isLoading) return "LOADING";
    if (!deals.length && !hasSearch) return "EMPTY_INITIAL";
    if (!filteredDeals.length && hasSearch) return "EMPTY_SEARCH";
    return "DATA";
  })();

  // ── Deal detail view ──
  if (selectedDeal) {
    return (
      <div className="w-full h-[calc(100vh-230px)] bg-[var(--bg-standard)] overflow-auto rounded-[12px]">
        <DealDetails deal={selectedDeal} onBack={() => setSelectedDeal(null)} />
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-230px)] bg-[var(--bg-standard)] flex flex-col gap-4 rounded-[12px] overflow-hidden">

      {/* ── LOADING (no existing data / search) ── */}
      {viewState === "LOADING" && !hasSearch && (
        <div className="flex h-full items-center justify-center">
          <Loader text="Loading deals..." />
        </div>
      )}

      {/* ── EMPTY INITIAL: ghost table + centered card overlay ── */}
      {viewState === "EMPTY_INITIAL" && (
        <div className="w-full relative flex-1 overflow-hidden">
          <NoDataTable searchBarPlaceholder="Search deals..." searchBarClassName="max-w-xs" />
          <NoDataCommonCard
            onClick={() => setShowCreateDeal(true)}
            title="No Deals Added"
            description="Start closing deals — add your first one now!"
            buttonText="Create Deal"
            image={moneyGif}
          />
        </div>
      )}

      {/* ── DATA / EMPTY_SEARCH / LOADING-with-search: real header always shown ── */}
      {(viewState === "DATA" ||
        viewState === "EMPTY_SEARCH" ||
        (viewState === "LOADING" && hasSearch)) && (
          <div className="flex flex-col gap-4 flex-1 overflow-auto">
            <DealsHeader
              openCount={openCount}
              totalValue={totalValue}
              setShowCreateDeal={setShowCreateDeal}
            />

            {/* Loading while searching */}
            {viewState === "LOADING" && hasSearch && (
              <div className="flex flex-1 items-center justify-center">
                <Loader text="Loading deals..." />
              </div>
            )}

            {/* No results for search */}
            {viewState === "EMPTY_SEARCH" && (
              <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
                No deals match your search.
              </div>
            )}

            {/* Error */}
            {isError && (
              <div className="flex flex-1 items-center justify-center text-sm text-error-500">
                Unable to load deals.
              </div>
            )}

            {/* Table */}
            {viewState === "DATA" && !isError && (
              <CustomTableDeals
                deals={filteredDeals}
                onDealClick={setSelectedDeal}
                onEdit={setDealToEdit}
                onDelete={setDealToDelete}
                selectedDeals={selectedDeals}
                setSelectedDeals={setSelectedDeals}
              />
            )}
          </div>
        )}

      {/* ── Create / Edit modal ── */}
      {(showCreateDeal || dealToEdit) && (
        <CreateDeal
          companyName={companyName}
          deal={dealToEdit}
          isSaving={dealToEdit ? isUpdating : isCreating}
          onSubmit={(values) =>
            dealToEdit
              ? updateDeal({ dealId: dealToEdit.id, ...values })
              : createDeal(values)
          }
          onClose={() => {
            setShowCreateDeal(false);
            setDealToEdit(null);
          }}
        />
      )}

      {/* ── Delete confirmation ── */}
      <DeleteConfirmationModal
        showConfirmDelete={Boolean(dealToDelete)}
        onClose={() => setDealToDelete(null)}
        title="Delete this deal?"
        description={`This will permanently delete ${dealToDelete?.name || "this deal"}.`}
        onDelete={() => deleteDeal({ dealId: dealToDelete.id })}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default Deals;
